import { useState, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCheck,
  Package,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  ExternalLink,
  Heart,
  PoundSterling,
  Mail,
  Pencil,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Eye,
  EyeOff,
  GripVertical,
  LucideIcon,
  Shield,
  Settings,
  Activity,
} from 'lucide-react';
import { useUIStore } from '@store/uiStore';
import { useSettingsStore, SidebarItemConfig } from '@store/settingsStore';
import { useAllModuleAccess } from '@utils/moduleAccess';
import { useAuthStore } from '@store/authStore';
import type { ModuleId } from '@utils/moduleAccess';

// ── Master definitions (source of truth) ────────────────────────────

interface NavDef {
  id: string;
  name: string;
  href: string;
  icon: LucideIcon;
}

interface ExtDef {
  id: string;
  name: string;
  href: string;
  icon: LucideIcon;
}

const ALL_NAV_ITEMS: NavDef[] = [
  { id: 'dashboard',     name: 'Dashboard',     href: '/',              icon: LayoutDashboard },
  { id: 'people',        name: 'People',        href: '/people',        icon: Users },
  { id: 'organisations', name: 'Organisations',  href: '/organisations', icon: Building2 },
  { id: 'volunteers',    name: 'Volunteers',    href: '/volunteers',    icon: UserCheck },
  { id: 'assets',        name: 'Assets',        href: '/assets',        icon: Package },
  { id: 'reports',       name: 'Reports',       href: '/reports',       icon: BarChart3 },
  { id: 'finance',       name: 'Finance',       href: '/finance',       icon: PoundSterling },
];

const ALL_EXTERNAL_ITEMS: ExtDef[] = [
  { id: 'beacon',    name: 'Beacon CRM', href: 'https://app.beaconcrm.org',    icon: Heart },
  { id: 'freeagent', name: 'FreeAgent',   href: 'https://scauk.freeagent.com', icon: PoundSterling },
  { id: 'mailchimp', name: 'MailChimp',   href: 'https://mailchimp.com',       icon: Mail },
];

// ── Helpers ─────────────────────────────────────────────────────────

/** Merge saved config with master list so new items appear and removed items vanish */
function mergeConfig<T extends { id: string }>(
  saved: SidebarItemConfig[] | null,
  master: T[],
): (T & { visible: boolean })[] {
  if (!saved) {
    return master.map((item) => ({ ...item, visible: true }));
  }

  // Build ordered result from saved config, skipping items no longer in master
  const masterMap = new Map(master.map((m) => [m.id, m]));
  const result: (T & { visible: boolean })[] = [];
  const seen = new Set<string>();

  for (const cfg of saved) {
    const def = masterMap.get(cfg.id);
    if (def) {
      result.push({ ...def, visible: cfg.visible });
      seen.add(cfg.id);
    }
  }

  // Append any new master items not in saved config
  for (const def of master) {
    if (!seen.has(def.id)) {
      result.push({ ...def, visible: true });
    }
  }

  return result;
}

// ── Component ───────────────────────────────────────────────────────

export default function Sidebar() {
  const location = useLocation();
  const { accounts } = useMsal();
  const { sidebarCollapsed, toggleSidebar, sidebarMobileOpen, setMobileSidebarOpen } = useUIStore();
  const avatarUrl = useSettingsStore((s) => s.avatarUrl);
  const sidebarNavOrder = useSettingsStore((s) => s.sidebarNavOrder);
  const sidebarExternalOrder = useSettingsStore((s) => s.sidebarExternalOrder);
  const setSidebarNavOrder = useSettingsStore((s) => s.setSidebarNavOrder);
  const setSidebarExternalOrder = useSettingsStore((s) => s.setSidebarExternalOrder);
  const resetSidebarConfig = useSettingsStore((s) => s.resetSidebarConfig);

  const [showCustomise, setShowCustomise] = useState(false);
  const { teamRole } = useAuthStore();
  const isAdmin = teamRole === 'Admin';

  const account = accounts[0];
  const initials =
    account?.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || 'U';

  // ── Resolved items (merged config + master) ─────────────────────

  const navItems = mergeConfig(sidebarNavOrder, ALL_NAV_ITEMS);
  const extItems = mergeConfig(sidebarExternalOrder, ALL_EXTERNAL_ITEMS);

  const moduleAccess = useAllModuleAccess();

  // Filter by user preference (visible) AND admin-configured module access (none = hidden)
  const visibleNav = navItems.filter((i) => {
    if (!i.visible) return false;
    const access = moduleAccess[i.id as ModuleId];
    return !access || access !== 'none';
  });
  const visibleExt = extItems.filter((i) => i.visible);

  // ── Customise helpers ───────────────────────────────────────────

  const moveItem = useCallback(
    (
      list: 'nav' | 'ext',
      index: number,
      direction: 'up' | 'down',
    ) => {
      const items = list === 'nav' ? [...navItems] : [...extItems];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= items.length) return;
      const temp = items[index]!;
      items[index] = items[target]!;
      items[target] = temp;
      const cfg = items.map((i) => ({ id: i.id, visible: i.visible }));
      if (list === 'nav') setSidebarNavOrder(cfg);
      else setSidebarExternalOrder(cfg);
    },
    [navItems, extItems, setSidebarNavOrder, setSidebarExternalOrder],
  );

  const toggleVisibility = useCallback(
    (list: 'nav' | 'ext', id: string) => {
      const items = list === 'nav' ? [...navItems] : [...extItems];
      const cfg = items.map((i) => ({
        id: i.id,
        visible: i.id === id ? !i.visible : i.visible,
      }));
      if (list === 'nav') setSidebarNavOrder(cfg);
      else setSidebarExternalOrder(cfg);
    },
    [navItems, extItems, setSidebarNavOrder, setSidebarExternalOrder],
  );

  // ── Sub-components ──────────────────────────────────────────────

  const NavItem = ({ item }: { item: NavDef }) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;

    return (
      <NavLink
        to={item.href}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative ${
          isActive
            ? 'bg-sca-purple-50 text-sca-purple-600'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        }`}
        onClick={() => setMobileSidebarOpen(false)}
      >
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sca-purple-500 rounded-r-full" />
        )}
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!sidebarCollapsed && <span>{item.name}</span>}
      </NavLink>
    );
  };

  const ExternalLinkItem = ({ item }: { item: ExtDef }) => {
    const Icon = item.icon;
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors ${
          sidebarCollapsed ? 'justify-center' : ''
        }`}
        onClick={() => setMobileSidebarOpen(false)}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        {!sidebarCollapsed && (
          <>
            <span className="flex-1">{item.name}</span>
            <ExternalLink className="h-3 w-3 text-gray-400" />
          </>
        )}
      </a>
    );
  };

  // ── Sidebar content ─────────────────────────────────────────────

  const sidebarContent = (
    <>
      {/* User profile */}
      <div className={`p-4 border-b border-gray-200 ${sidebarCollapsed ? 'px-3' : ''}`}>
        <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={account?.name || 'User'}
              className="h-10 w-10 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-sca-purple-500 flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
              {initials}
            </div>
          )}
          {!sidebarCollapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {account?.name || 'User'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleNav.map((item) => (
          <NavItem key={item.id} item={item} />
        ))}

        {/* Admin Section */}
        {isAdmin && (
          <div className="pt-4 mt-4 border-t border-gray-200">
            {!sidebarCollapsed && (
              <p className="px-3 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Admin
              </p>
            )}
            <div className="space-y-0.5">
              {[
                { href: '/system-status', name: 'System Status', icon: Activity },
                { href: '/team-management', name: 'Team Management', icon: Users },
                { href: '/system-settings', name: 'System Settings', icon: Settings },
              ].map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                      isActive
                        ? 'bg-sca-purple-50 text-sca-purple-600'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setMobileSidebarOpen(false)}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sca-purple-500 rounded-r-full" />
                    )}
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    {!sidebarCollapsed && <span>{item.name}</span>}
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {/* External Systems Section */}
        {visibleExt.length > 0 && (
          <div className="pt-4 mt-4 border-t border-gray-200">
            {!sidebarCollapsed && (
              <p className="px-3 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                External Systems
              </p>
            )}
            <div className="space-y-0.5">
              {visibleExt.map((item) => (
                <ExternalLinkItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom: Customise + Collapse */}
      <div className="p-3 border-t border-gray-200 space-y-1">
        <button
          onClick={() => setShowCustomise(true)}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors ${
            sidebarCollapsed ? 'justify-center' : ''
          }`}
          title="Customise sidebar"
        >
          <Pencil className="h-4 w-4 flex-shrink-0" />
          {!sidebarCollapsed && <span>Customise</span>}
        </button>

        <button
          onClick={toggleSidebar}
          className="hidden lg:flex items-center justify-center w-full p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <ChevronLeft className="h-5 w-5" />
          )}
        </button>
      </div>
    </>
  );

  // ── Customise Modal ─────────────────────────────────────────────

  const CustomiseRow = ({
    icon: Icon,
    name,
    visible,
    index,
    total,
    onToggle,
    onMove,
  }: {
    icon: LucideIcon;
    name: string;
    visible: boolean;
    index: number;
    total: number;
    onToggle: () => void;
    onMove: (dir: 'up' | 'down') => void;
  }) => (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
        visible ? 'bg-white' : 'bg-gray-50 opacity-60'
      }`}
    >
      <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
      <Icon className="h-4 w-4 text-gray-500 flex-shrink-0" />
      <span className={`flex-1 text-sm ${visible ? 'text-gray-900' : 'text-gray-400'}`}>
        {name}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onMove('up')}
          disabled={index === 0}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move up"
        >
          <ArrowUp className="h-3.5 w-3.5 text-gray-500" />
        </button>
        <button
          onClick={() => onMove('down')}
          disabled={index === total - 1}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Move down"
        >
          <ArrowDown className="h-3.5 w-3.5 text-gray-500" />
        </button>
        <button
          onClick={onToggle}
          className="p-1 rounded hover:bg-gray-100"
          title={visible ? 'Hide' : 'Show'}
        >
          {visible ? (
            <Eye className="h-3.5 w-3.5 text-sca-purple-500" />
          ) : (
            <EyeOff className="h-3.5 w-3.5 text-gray-400" />
          )}
        </button>
      </div>
    </div>
  );

  const customiseModal = showCustomise && (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Customise Sidebar</h2>
          <button
            onClick={() => setShowCustomise(false)}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          <p className="text-sm text-gray-500">
            Show, hide, and reorder your sidebar navigation. Changes are saved automatically.
          </p>

          {/* Navigation items */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
              Navigation
            </h3>
            <div className="space-y-1">
              {navItems.map((item, idx) => (
                <CustomiseRow
                  key={item.id}
                  icon={item.icon}
                  name={item.name}
                  visible={item.visible}
                  index={idx}
                  total={navItems.length}
                  onToggle={() => toggleVisibility('nav', item.id)}
                  onMove={(dir) => moveItem('nav', idx, dir)}
                />
              ))}
            </div>
          </div>

          {/* External links */}
          <div>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
              External Systems
            </h3>
            <div className="space-y-1">
              {extItems.map((item, idx) => (
                <CustomiseRow
                  key={item.id}
                  icon={item.icon}
                  name={item.name}
                  visible={item.visible}
                  index={idx}
                  total={extItems.length}
                  onToggle={() => toggleVisibility('ext', item.id)}
                  onMove={(dir) => moveItem('ext', idx, dir)}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button
            onClick={() => {
              resetSidebarConfig();
            }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to default
          </button>
          <button
            onClick={() => setShowCustomise(false)}
            className="btn-primary text-sm px-4 py-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────

  return (
    <>
      {/* Mobile menu button - positioned in the TopBar area */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="lg:hidden fixed top-3 left-4 z-[60] p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10"
        aria-label="Open menu"
      >
        <Menu className="h-6 w-6" />
      </button>

      {/* Mobile overlay */}
      {sidebarMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar - starts below the top bar */}
      <aside
        className={`lg:hidden fixed top-14 bottom-0 left-0 z-50 w-sidebar bg-white flex flex-col transform transition-transform duration-300 ${
          sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          onClick={() => setMobileSidebarOpen(false)}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
        {sidebarContent}
      </aside>

      {/* Desktop sidebar - starts below the top bar */}
      <aside
        className={`hidden lg:flex fixed top-14 bottom-0 left-0 z-30 bg-white border-r border-gray-200 flex-col transition-all duration-300 ${
          sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Customise modal */}
      {customiseModal}
    </>
  );
}
